import { useMemo } from 'react';

import { DateRange } from 'react-day-picker';

import useAnalytics, { EMetric } from 'api/useAnalytics';
import {
  ANALYTICS,
  getWholeRange,
  FINANCE_ANALYTICS,
  BUSINESS_ANALYTICS,
  DEFAULT_DATE_RANGE,
  isDateStringHourly,
  BREAKDOWNS_ANALYTICS,
  BUSINESS_BREAKDOWNS_ANALYTICS
} from '../utils';

export function useAnalyticsData(
  apiDateRange: DateRange | undefined,
  apiPreviousDateRange: DateRange | undefined,
  publisherId: string | undefined,
  shouldDisplaySciPlayAnalytics: boolean
) {
  const analyticsToQuery = shouldDisplaySciPlayAnalytics
    ? ANALYTICS
    : BUSINESS_ANALYTICS;
  const breakdownsToQuery = shouldDisplaySciPlayAnalytics
    ? BREAKDOWNS_ANALYTICS
    : BUSINESS_BREAKDOWNS_ANALYTICS;
  const analytics = useAnalytics(
    apiDateRange?.from?.toISOString() ??
      getWholeRange(DEFAULT_DATE_RANGE)!.from!.toISOString(),
    apiDateRange?.to?.toISOString() ??
      getWholeRange(DEFAULT_DATE_RANGE)!.to!.toISOString(),
    analyticsToQuery,
    analyticsToQuery,
    breakdownsToQuery,
    publisherId
  );

  const previousAnalytics = useAnalytics(
    apiPreviousDateRange?.from?.toISOString(),
    apiPreviousDateRange?.to?.toISOString(),
    analyticsToQuery,
    analyticsToQuery,
    breakdownsToQuery,
    publisherId
  );

  const isLoading = useMemo(() => {
    return (
      analytics.getAnalytics.isLoading ||
      analytics.getTrends.isLoading ||
      previousAnalytics.getAnalytics.isLoading ||
      previousAnalytics.getTrends.isLoading
    );
  }, [analytics, previousAnalytics]);

  const filteredAnalytics = useMemo(() => {
    const firstTrendDate = analytics.getTrends?.data?.[0]?.values?.[0]?.date;
    return ANALYTICS.filter((metricName) => {
      if (metricName === EMetric.TOTAL_REVENUE) return false;
      if (
        firstTrendDate &&
        isDateStringHourly(firstTrendDate) &&
        metricName === EMetric.DAU
      )
        return false;
      return true;
    }).reduce(
      (
        acc: { businessAnalytics: EMetric[]; financeAnalytics: EMetric[] },
        metricName
      ) => {
        if (BUSINESS_ANALYTICS.includes(metricName)) {
          acc.businessAnalytics.push(metricName);
        } else if (FINANCE_ANALYTICS.includes(metricName)) {
          acc.financeAnalytics.push(metricName);
        }
        return acc;
      },
      { businessAnalytics: [], financeAnalytics: [] }
    );
  }, [analytics.getTrends?.data]);

  return {
    filteredAnalytics,
    analytics,
    previousAnalytics,
    isLoading
  };
}
